import { useContext, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { fetchData } from '../../../global/utils/fetch'
import { createCategory, updateCategory } from '../../api'
import { CategoryListItem } from '../category-table/CategoryTable'
import styles from './styles.module.css'

interface CategoryModalProps {
  isOpen: boolean
  onClose: () => void
  onSaveComplete: () => void
  editingCategory?: CategoryListItem | null
}

function CategoryModal({ isOpen, onClose, onSaveComplete, editingCategory }: CategoryModalProps) {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [examples, setExamples] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      if (editingCategory) {
        setName(editingCategory.name)
        setDescription(editingCategory.description || '')
        setExamples(editingCategory.examples || [])
      } else {
        setName('')
        setDescription('')
        setExamples([])
      }
    }
  }, [isOpen, editingCategory])

  function handleClose() {
    if (isSaving) return
    onClose()
  }

  function handleAddExample() {
    setExamples([...examples, ''])
  }

  function handleExampleChange(index: number, value: string) {
    const updated = [...examples]
    updated[index] = value
    setExamples(updated)
  }

  function handleRemoveExample(index: number) {
    setExamples(examples.filter((_, i) => i !== index))
  }

  async function handleSave() {
    if (!name.trim()) return

    setIsSaving(true)

    const filteredExamples = examples.filter((e) => e.trim())

    try {
      if (editingCategory) {
        await fetchData(
          updateCategory(editingCategory.objectId, {
            name: name.trim(),
            description: description.trim() || undefined,
            examples: filteredExamples.length ? filteredExamples : undefined,
          }),
        )
        setToastStatus({
          ...toastStatus,
          isOpen: true,
          message: 'Category updated.',
          severity: 'success',
        })
      } else {
        await fetchData(
          createCategory({
            name: name.trim(),
            description: description.trim() || undefined,
            examples: filteredExamples.length ? filteredExamples : undefined,
          }),
        )
        setToastStatus({
          ...toastStatus,
          isOpen: true,
          message: 'Category created.',
          severity: 'success',
        })
      }
      onSaveComplete()
      onClose()
    } catch (error: any) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Failed to save category.',
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box className={styles.modalContainer}>
        <div className={styles.title}>
          {editingCategory ? 'Edit Category' : 'Add Category'}
        </div>

        <div className={styles.formContainer}>
          <TextField
            label='Category Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size='small'
            disabled={isSaving}
          />

          <TextField
            label='Description (optional)'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size='small'
            multiline
            rows={2}
            disabled={isSaving}
          />

          <div className={styles.examplesSection}>
            <div className={styles.examplesHeader}>
              <span className={styles.examplesLabel}>Example Questions</span>
              <IconButton size='small' onClick={handleAddExample} disabled={isSaving}>
                <AddIcon fontSize='small' />
              </IconButton>
            </div>
            {examples.map((example, index) => (
              <div key={index} className={styles.exampleRow}>
                <TextField
                  value={example}
                  onChange={(e) => handleExampleChange(index, e.target.value)}
                  fullWidth
                  size='small'
                  placeholder={`Example question ${index + 1}`}
                  disabled={isSaving}
                />
                <IconButton
                  size='small'
                  onClick={() => handleRemoveExample(index)}
                  disabled={isSaving}
                >
                  <CloseIcon fontSize='small' />
                </IconButton>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.buttonContainer}>
          <SecondaryButton
            buttonName='Cancel'
            onClick={handleClose}
            disabled={isSaving}
          />
          <PrimaryButton
            buttonName={editingCategory ? 'Save' : 'Create'}
            onClick={handleSave}
            disabled={!name.trim() || isSaving}
          />
        </div>
      </Box>
    </Modal>
  )
}

export default CategoryModal
